/**
 * /ti/labels — One page for all 80 bin QR labels.
 * 10 codes per A4 sheet · 2 sheets per department · code name only (no URLs).
 */
import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { Download, Printer } from "lucide-react";

const BASE_URL = "https://manage.thetabletop.in";
const PINK = "#EE2677";

const ZONES = [
  { id: "baking", prefix: "B", label: "Baking" },
  { id: "frosting", prefix: "F", label: "Frosting" },
  { id: "decor", prefix: "D", label: "Decor" },
  { id: "packaging", prefix: "P", label: "Packaging" },
];

const LABELS_PER_SHEET = 10;
const GRID_COLS = 2;
const GRID_ROWS = 5;

function codesForSheet(prefix, sheetIndex) {
  const start = sheetIndex * LABELS_PER_SHEET + 1;
  return Array.from({ length: LABELS_PER_SHEET }, (_, i) => `${prefix}${start + i}`);
}

function allSheetsForZone(zone) {
  return [0, 1].map((sheetIndex) => ({
    sheetIndex,
    sheetNum: sheetIndex + 1,
    codes: codesForSheet(zone.prefix, sheetIndex),
    title: `${zone.label} — ${zone.prefix}${sheetIndex * LABELS_PER_SHEET + 1}–${zone.prefix}${(sheetIndex + 1) * LABELS_PER_SHEET}`,
  }));
}

const PAGE_CSS = `
  .ti-labels-page {
    font-family: system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;
    background: #F5F2EA; min-height: 100dvh; color: #2D2D2D;
  }
  .ti-labels-screen {
    max-width: 720px; margin: 0 auto; padding: 20px 16px 40px;
  }
  .ti-labels-header { text-align: center; margin-bottom: 20px; }
  .ti-labels-title { font-size: 22px; font-weight: 900; color: ${PINK}; margin: 0 0 8px; }
  .ti-labels-subtitle { font-size: 13px; color: rgba(45,45,45,0.6); font-weight: 600; line-height: 1.5; margin: 0; }
  .ti-labels-actions {
    display: flex; flex-direction: column; gap: 10px; margin-bottom: 28px;
  }
  .ti-labels-action-btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
    border: none; border-radius: 14px; padding: 14px 18px;
    font-size: 15px; font-weight: 800; cursor: pointer; width: 100%;
  }
  .ti-labels-action-btn:disabled { opacity: 0.55; cursor: wait; }
  .ti-labels-action-btn--primary { background: ${PINK}; color: white; }
  .ti-labels-action-btn--secondary { background: white; color: ${PINK}; border: 2px solid ${PINK}; }
  .ti-labels-dept-block { margin-bottom: 24px; }
  .ti-labels-dept-name {
    font-size: 16px; font-weight: 900; margin: 0 0 10px; color: #2D2D2D;
  }
  .ti-labels-sheet-preview {
    background: white; border-radius: 12px; padding: 12px; margin-bottom: 12px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.06); border: 1px solid #E5E7EB;
  }
  .ti-labels-sheet-tag {
    font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.06em;
    color: rgba(45,45,45,0.45); margin-bottom: 10px;
  }
  .ti-labels-grid {
    display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px;
  }
  .ti-labels-cell {
    border: 2px solid #111827; border-radius: 10px; padding: 10px 8px 8px;
    text-align: center; background: white;
  }
  .ti-labels-cell-canvas { display: block; margin: 0 auto; width: 100%; max-width: 110px; height: auto; }
  .ti-labels-cell-code { font-size: 22px; font-weight: 900; margin-top: 6px; letter-spacing: 0.04em; color: #111827; }
  .ti-labels-cell-zone { font-size: 9px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.08em; color: rgba(45,45,45,0.45); margin-top: 2px; }
  .ti-labels-print-root { display: none; }

  @media print {
    body { background: white; margin: 0; }
    .ti-labels-screen { display: none !important; }
    .ti-labels-print-root { display: block !important; }
    .ti-labels-a4 {
      width: 100%; min-height: 100vh; page-break-after: always;
      padding: 10mm 12mm; box-sizing: border-box; background: white;
    }
    .ti-labels-a4:last-child { page-break-after: auto; }
    .ti-labels-a4-head {
      font-size: 14px; font-weight: 900; margin-bottom: 8mm; color: #111827;
      border-bottom: 2px solid #111827; padding-bottom: 3mm;
    }
    .ti-labels-a4-grid {
      display: grid; grid-template-columns: repeat(2, 1fr); gap: 6mm;
      height: calc(100% - 16mm);
    }
    .ti-labels-a4-cell {
      border: 1.5pt solid #111827; border-radius: 4mm; padding: 4mm;
      display: flex; flex-direction: column; align-items: center; justify-content: center;
      break-inside: avoid; page-break-inside: avoid;
    }
    .ti-labels-a4-cell img, .ti-labels-a4-cell canvas { width: 38mm !important; height: 38mm !important; }
    .ti-labels-a4-code { font-size: 20pt; font-weight: 900; margin-top: 3mm; letter-spacing: 0.05em; }
    .ti-labels-a4-zone { font-size: 8pt; font-weight: 800; text-transform: uppercase; letter-spacing: 0.1em; color: #6B7280; margin-top: 1mm; }
  }
`;

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function triggerDownload(href, filename) {
  const link = document.createElement("a");
  link.download = filename;
  link.href = href;
  link.click();
}

async function qrDataUrl(code, size) {
  return QRCode.toDataURL(`${BASE_URL}/ti/s/${code}`, {
    width: size,
    margin: 1,
    color: { dark: "#000000", light: "#FFFFFF" },
    errorCorrectionLevel: "M",
  });
}

async function renderA4Canvas(zone, sheetIndex) {
  const A4_W = 1240;
  const A4_H = 1754;
  const codes = codesForSheet(zone.prefix, sheetIndex);
  const canvas = document.createElement("canvas");
  canvas.width = A4_W;
  canvas.height = A4_H;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, A4_W, A4_H);

  const headY = 56;
  ctx.fillStyle = "#111827";
  ctx.font = "bold 32px system-ui,sans-serif";
  ctx.textAlign = "left";
  ctx.fillText(`Table Top Inventory — ${zone.label}`, 48, headY);
  ctx.font = "600 20px system-ui,sans-serif";
  ctx.fillStyle = "#6B7280";
  ctx.fillText(
    `Sheet ${sheetIndex + 1} of 2 · ${codes[0]} to ${codes[codes.length - 1]}`,
    48,
    headY + 32
  );

  ctx.strokeStyle = "#111827";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(48, headY + 48);
  ctx.lineTo(A4_W - 48, headY + 48);
  ctx.stroke();

  const gridTop = headY + 72;
  const gridH = A4_H - gridTop - 48;
  const cellW = (A4_W - 96 - 24) / GRID_COLS;
  const cellH = gridH / GRID_ROWS;
  const qrSize = Math.min(cellW - 48, cellH - 100, 200);

  for (let i = 0; i < codes.length; i += 1) {
    const code = codes[i];
    const col = i % GRID_COLS;
    const row = Math.floor(i / GRID_COLS);
    const cellX = 48 + col * (cellW + 24);
    const cellY = gridTop + row * cellH;

    ctx.strokeStyle = "#111827";
    ctx.lineWidth = 2;
    ctx.strokeRect(cellX, cellY, cellW, cellH - 8);

    const dataUrl = await qrDataUrl(code, Math.round(qrSize * 2));
    const img = await loadImage(dataUrl);
    const qrX = cellX + (cellW - qrSize) / 2;
    const qrY = cellY + 16;
    ctx.drawImage(img, qrX, qrY, qrSize, qrSize);

    ctx.fillStyle = "#111827";
    ctx.font = "bold 36px system-ui,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(code, cellX + cellW / 2, qrY + qrSize + 40);

    ctx.fillStyle = "#6B7280";
    ctx.font = "bold 14px system-ui,sans-serif";
    ctx.fillText(zone.label.toUpperCase(), cellX + cellW / 2, qrY + qrSize + 62);
  }

  return canvas;
}

async function downloadDepartment(zone) {
  for (let sheet = 0; sheet < 2; sheet += 1) {
    const c = await renderA4Canvas(zone, sheet);
    triggerDownload(c.toDataURL("image/png"), `ti-${zone.id}-sheet-${sheet + 1}.png`);
    await new Promise((r) => setTimeout(r, 400));
  }
}

async function downloadAllDepartments() {
  for (const zone of ZONES) {
    await downloadDepartment(zone);
    await new Promise((r) => setTimeout(r, 600));
  }
}

function QrCell({ code, zoneLabel }) {
  const canvasRef = useRef(null);
  const [qErr, setQErr] = useState("");

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    QRCode.toCanvas(canvas, `${BASE_URL}/ti/s/${code}`, {
      width: 110,
      margin: 1,
      color: { dark: "#000000", light: "#FFFFFF" },
      errorCorrectionLevel: "M",
    }).catch((e) => setQErr(e.message || "QR error"));
  }, [code]);

  return (
    <div className="ti-labels-cell">
      <canvas ref={canvasRef} className="ti-labels-cell-canvas" />
      <div className="ti-labels-cell-code">{code}</div>
      <div className="ti-labels-cell-zone">{zoneLabel}</div>
      {qErr ? <div style={{ color: "#B91C1C", fontSize: 10 }}>{qErr}</div> : null}
    </div>
  );
}

function PrintSheet({ zone, sheetIndex, qrImages }) {
  const codes = codesForSheet(zone.prefix, sheetIndex);
  return (
    <div className="ti-labels-a4">
      <div className="ti-labels-a4-head">
        Table Top Inventory — {zone.label} · Sheet {sheetIndex + 1} of 2 · {codes[0]}–{codes[codes.length - 1]}
      </div>
      <div className="ti-labels-a4-grid">
        {codes.map((code) => (
          <div key={code} className="ti-labels-a4-cell">
            {qrImages[code] ? (
              <img src={qrImages[code]} alt="" width={144} height={144} />
            ) : null}
            <div className="ti-labels-a4-code">{code}</div>
            <div className="ti-labels-a4-zone">{zone.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function InventoryLabelsPrintPage() {
  const [busy, setBusy] = useState("");
  const [printImages, setPrintImages] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const imgs = {};
      for (const zone of ZONES) {
        for (let sheet = 0; sheet < 2; sheet += 1) {
          for (const code of codesForSheet(zone.prefix, sheet)) {
            imgs[code] = await qrDataUrl(code, 280);
          }
        }
      }
      if (!cancelled) setPrintImages(imgs);
    })();
    return () => { cancelled = true; };
  }, []);

  const onDownloadAll = async () => {
    if (busy) return;
    setBusy("download");
    try {
      await downloadAllDepartments();
    } finally {
      setBusy("");
    }
  };

  const onPrintAll = () => {
    window.print();
  };

  return (
    <div className="ti-labels-page">
      <style>{PAGE_CSS}</style>

      <div className="ti-labels-screen">
        <div className="ti-labels-header">
          <h1 className="ti-labels-title">Inventory Bin QR Labels</h1>
          <p className="ti-labels-subtitle">
            80 labels · 4 departments · 10 per A4 sheet · Print once, paste on bins
          </p>
        </div>

        <div className="ti-labels-actions">
          <button
            type="button"
            className="ti-labels-action-btn ti-labels-action-btn--primary"
            onClick={onDownloadAll}
            disabled={Boolean(busy)}
          >
            <Download size={18} aria-hidden />
            {busy === "download" ? "Preparing downloads…" : "Download all 4 departments (8 PNG sheets)"}
          </button>
          <button
            type="button"
            className="ti-labels-action-btn ti-labels-action-btn--secondary"
            onClick={onPrintAll}
            disabled={Boolean(busy)}
          >
            <Printer size={18} aria-hidden />
            Print all labels
          </button>
        </div>

        {ZONES.map((zone) => (
          <div key={zone.id} className="ti-labels-dept-block">
            <h2 className="ti-labels-dept-name">{zone.label} ({zone.prefix}1–{zone.prefix}20)</h2>
            {allSheetsForZone(zone).map((sheet) => (
              <div key={sheet.sheetNum} className="ti-labels-sheet-preview">
                <div className="ti-labels-sheet-tag">A4 sheet {sheet.sheetNum} of 2 — {sheet.title}</div>
                <div className="ti-labels-grid">
                  {sheet.codes.map((code) => (
                    <QrCell key={code} code={code} zoneLabel={zone.label} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      <div className="ti-labels-print-root" aria-hidden="true">
        {ZONES.flatMap((zone) =>
          [0, 1].map((sheetIndex) => (
            <PrintSheet
              key={`${zone.id}-${sheetIndex}`}
              zone={zone}
              sheetIndex={sheetIndex}
              qrImages={printImages}
            />
          ))
        )}
      </div>
    </div>
  );
}
